<script setup lang="ts">
    import { computed, ref, onMounted, watch, type Ref } from 'vue'
    import { onBeforeRouteLeave, useRoute } from 'vue-router'

    import PortfolioNavigation from '@common/Components/PortfolioNavigation.vue'
    import ProjectNavigation from '@Quest/ProjectPlans/Components/ProjectNavigation.vue'

    import { useProjectPhasesQueryV1 } from '@Quest/ProjectPlans/queries-vue-composables'
    import { useSetupPhasesCommandV1, useClearPhasesCommandV1, useSetPhaseDurationCommandV1 } from '@Quest/ProjectPlans/commands-vue-composables'

    import type { ProjectPhasesReadModel } from '@Quest/ProjectPlans/readmodels'

    const route = useRoute()

    const workspaceId = computed(() => Number(route.params.workspaceId ?? 0))
    const tchProjectId = computed(() => Number(route.params.tchProjectId ?? 0))
    const projectId = computed(() => String(route.params.projectId ?? ''))
    const planId = ref<string | null>(null)

    const projectPlanName = computed<string>(() => rm.value?.ProjectPlanName ?? '')

    const {
        runProjectPhasesQueryV1,
        modelProjectPhasesQueryV1
    } = useProjectPhasesQueryV1(workspaceId, tchProjectId, projectId, planId)

    const rm = modelProjectPhasesQueryV1 as Ref<ProjectPhasesReadModel | null>

    const userId = computed(() => rm.value?.UserId)
    const portfolioId = computed(() => rm.value?.PortfolioId ?? '')

    const {
        invokeSetupPhasesCommandV1,
        validationErrorsSetupPhasesCommandV1,
        onSucceedSetupPhasesCommandV1
    } = useSetupPhasesCommandV1(userId, workspaceId, portfolioId, projectId, planId)

    const {
        invokeClearPhasesCommandV1,
        validationErrorsClearPhasesCommandV1,
        onSucceedClearPhasesCommandV1
    } = useClearPhasesCommandV1(userId, workspaceId, portfolioId, projectId, planId)

    const {
        invokeSetPhaseDurationCommandV1,
        validationErrorsSetPhaseDurationCommandV1,
        onSucceedSetPhaseDurationCommandV1
    } = useSetPhaseDurationCommandV1(userId, workspaceId, portfolioId, projectId, planId)

    const durations = ref<Record<string, number>>({})

    const orderedPhases = computed(() =>
    {
        if (!rm.value) return []
        return [...rm.value.Phases].sort((a, b) => a.OrderValue - b.OrderValue)
    })

    const totalMonths = computed(() =>
    {
        return Object.values(durations.value).reduce((a, b) => a + (b ?? 0), 0)
    })

    function setupPhases()
    {
        const map: Record<string, number> = {}

        for (const p of orderedPhases.value)
        {
            const duration = durations.value[p.Id] ?? 0

            if (duration > 0)
                map[p.Id] = duration
        }

        invokeSetupPhasesCommandV1({
            phaseDurations: map
        })
    }

    function setPhaseDuration(phaseId: string)
    {

        invokeSetPhaseDurationCommandV1({
            projectPhaseDefinitionId: phaseId,
            durationInMonths: durations.value[phaseId] ?? 0
        })
    }

    function clearPhases()
    {
        invokeClearPhasesCommandV1()
    }

    onMounted(() =>
    {

        document.title = 'Phases | TransparentChoice'

        planId.value = route.params.planId
            ? String(route.params.planId)
            : null

        runProjectPhasesQueryV1()
    })

    watch(rm, (value) =>
    {

        if (!value) return

        planId.value = value.ProjectPlanId

        const map: Record<string, number> = {}

        for (const p of value.Phases)
        {
            map[p.Id] = p.DurationInMonths ?? 0
        }

        durations.value = map
    })

    onSucceedSetupPhasesCommandV1(() => runProjectPhasesQueryV1())
    onSucceedClearPhasesCommandV1(() => runProjectPhasesQueryV1())
    onSucceedSetPhaseDurationCommandV1(() => runProjectPhasesQueryV1())

</script>


<template>

    <div>

        <PortfolioNavigation :workspaceId="workspaceId"
                             :projectId="tchProjectId" />

        <div v-if="rm" class="container-fluid" style="margin-top:80px;padding-top:40px">

            <div class="row">

                <div class="col-lg-6">
                    <h1 style="font-size:2em;font-weight:300;color:#33475b;margin:0;">
                        {{ rm.ProjectName }}
                    </h1>
                </div>

                <div class="col-lg-6 text-right align-self-end">

                    <a class="btn btn-outline-secondary" style="padding: 0.7em 2em;"
                       v-sanitizedHref="`/#/portfolio/alternative/dashboard/${workspaceId}/${tchProjectId}/${projectId}`">
                        Meta data
                    </a>
                </div>

            </div>

            <hr />

            <ProjectNavigation ActivePage="phases"
                               class="mt-4"
                               :workspaceId="workspaceId"
                               :tchProjectId="tchProjectId"
                               :projectId="projectId"
                               :planId="planId" />


            <div class="row mt-4">

                <div class="col-lg-8">

                    <form @submit.prevent="setupPhases">

                        <div class="d-flex justify-content-between align-items-center mb-3">

                            <h3 class="mb-0">Project phases</h3>

                            <button v-if="rm.HasPhaseDurations"
                                    type="button"
                                    class="btn btn-outline-danger"
                                    @click="clearPhases">
                                Clear phases
                            </button>

                        </div>

                        <table class="table table-bordered align-middle">

                            <thead>
                                <tr>
                                    <th>Phase</th>
                                    <th style="width:160px">Duration (months)</th>
                                    <th v-if="rm.HasPhaseDurations" style="width:120px"></th>
                                </tr>
                            </thead>

                            <tbody>

                                <tr v-for="p in orderedPhases" :key="p.Id">

                                    <td>
                                        {{ p.Name }}
                                    </td>

                                    <td>

                                        <input type="number"
                                               min="0"
                                               max="240"
                                               class="form-control"
                                               v-model.number="durations[p.Id]" />

                                    </td>

                                    <td v-if="rm.HasPhaseDurations">

                                        <button type="button"
                                                class="btn btn-outline-primary btn-sm"
                                                @click="setPhaseDuration(p.Id)">
                                            Save
                                        </button>

                                    </td>

                                </tr>

                            </tbody>

                        </table>

                        <div class="d-flex justify-content-between align-items-center mt-3">

                            <div class="text-muted">
                                Total duration: {{ totalMonths }} months
                            </div>

                            <button v-if="!rm.HasPhaseDurations"
                                    class="btn btn-primary">
                                Setup phases
                            </button>

                        </div>

                    </form>

                </div>

            </div>


            <div v-if="validationErrorsSetupPhasesCommandV1?.length" class="alert alert-danger mt-3">
                <div v-for="e in validationErrorsSetupPhasesCommandV1" :key="e.Message">
                    {{ e.Message }}
                </div>
            </div>

            <div v-if="validationErrorsSetPhaseDurationCommandV1?.length" class="alert alert-danger mt-3">
                <div v-for="e in validationErrorsSetPhaseDurationCommandV1" :key="e.Message">
                    {{ e.Message }}
                </div>
            </div>

            <div v-if="validationErrorsClearPhasesCommandV1?.length" class="alert alert-danger mt-3">
                <div v-for="e in validationErrorsClearPhasesCommandV1" :key="e.Message">
                    {{ e.Message }}
                </div>
            </div>


        </div>

    </div>

</template>


<style scoped>

    table input {
        text-align: center;
    }
</style>